/**
 * Carga do vendedor (página própria, /cargas/:id): resumo, acerto em três etapas, carregamentos
 * e vendas, histórico e auditoria, relatório. A etapa do acerto e o que falta vêm da API.
 */
import {
  LOAD_STATUS_LABEL,
  PAYMENT_HANDLING_LABEL,
  SETTLEMENT_ORIGIN_LABEL,
  SETTLEMENT_STAGE_LABEL,
  SETTLEMENT_STATUS_LABEL,
  formatMoney,
  shipmentInputSchema,
  type LoadDetail,
  type LoadReport,
  type LoadStatus,
  type Settlement,
} from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useCan } from "@/app/me";
import { Alert, Badge, Button, Field, Input, Spinner, Table } from "@/components/ui";
import { EmptyState, SummaryCard, Tabs } from "@/components/patterns";
import { apiFetch, errorMessage } from "@/lib/api";
import { SaveError } from "../cadastros/widgets";
import { DocItemsEditor, docItemsBody, emptyDocItem, type DocItemLine } from "../estoque/DocItems";
import { dateTimeBR, qty, useDocAction } from "../estoque/lib";
import { DocStatusBadge, ReverseAction } from "../estoque/pickers";
import { MovementsTable } from "../estoque/SaldosPage";
import { EmissionPanel } from "../fiscal/EmissionPanel";
import { ORDER_KEY } from "../vendas/lib";
import { SalesStatusBadge } from "../vendas/pickers";
import { AcertoPanel } from "./AcertoPanel";
import { divergences, settlementTotals } from "./acerto-lib";
import { LOAD_KEY } from "./lib";

const INVALIDATE = [LOAD_KEY, ["estoque"], ORDER_KEY];
const LOAD_TONE: Record<LoadStatus, "neutral" | "ok" | "warn" | "error"> = {
  aberta: "warn",
  acertada: "ok",
  cancelada: "neutral",
};
const money = (v: string | null | undefined) =>
  v === null || v === undefined ? "—" : formatMoney(v);

export function CargaPage({ id }: { id: string }) {
  const load = useQuery({
    queryKey: [...LOAD_KEY, "detalhe", id],
    queryFn: () => apiFetch<LoadDetail>(`/cargas/${id}`),
  });
  const [tab, setTab] = useState<string | null>(null);
  if (load.error) {
    return (
      <div className="space-y-4">
        <BackLink />
        <Alert tone="error">{errorMessage(load.error)}</Alert>
      </div>
    );
  }
  const l = load.data;
  if (!l) return <Spinner />;
  const s = l.settlement;
  const shipped = l.shipments.some((sh) => sh.status === "confirmado");
  const badge =
    l.status === "aberta"
      ? s.stage === "retorno" && s.id === null
        ? { tone: "warn" as const, text: LOAD_STATUS_LABEL.aberta }
        : { tone: "warn" as const, text: `Acerto: ${SETTLEMENT_STAGE_LABEL[s.stage]}` }
      : { tone: LOAD_TONE[l.status], text: LOAD_STATUS_LABEL[l.status] };
  const initial = tab ?? (shipped || l.status !== "aberta" ? "acerto" : "carregamentos");
  return (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <BackLink />
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight">
            Carga {l.number} · {l.salespersonName}
          </h1>
          <Badge tone={badge.tone}>{badge.text}</Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          {l.originWarehouseName} → {l.warehouseName} · aberta em {dateTimeBR(l.openedAt)}
          {l.settledAt ? ` · acertada em ${dateTimeBR(l.settledAt)}` : ""}
          {s.origin ? ` · ${SETTLEMENT_ORIGIN_LABEL[s.origin].toLowerCase()}` : ""}
        </p>
        {l.salespersonLinkStatus === "historico_incerto" && l.status === "aberta" && (
          <p className="text-xs text-muted-foreground">
            Carga anterior ao acerto simplificado sem registro confiável do vendedor: quem confirmar
            a conferência não finaliza.
          </p>
        )}
      </div>
      {l.status === "cancelada" && (
        <Alert tone="warn">
          Cancelada em {dateTimeBR(l.cancelledAt)}. Motivo: {l.cancelReason}
        </Alert>
      )}
      {shipped && <SummaryCards l={l} />}
      <Tabs
        key={initial}
        label="Seções da carga"
        initial={initial}
        tabs={[
          {
            key: "acerto",
            label: "Acerto",
            content: shipped ? (
              <AcertoPanel
                l={l}
                onShowReport={() => setTab("relatorio")}
                finishedExtra={
                  <div className="space-y-3">
                    <SettlementFiscal s={s} />
                    {l.status === "acertada" && <ReverseSettlementGate l={l} />}
                  </div>
                }
              />
            ) : (
              <EmptyState
                title="Nada carregado ainda"
                description="O acerto começa depois do primeiro carregamento confirmado (aba Carregamentos e vendas)."
              />
            ),
          },
          {
            key: "carregamentos",
            label: "Carregamentos e vendas",
            badge: l.shipments.length + l.orders.length,
            content: (
              <div className="space-y-6">
                <Shipments l={l} />
                <Orders l={l} />
                <CancelLoad l={l} />
              </div>
            ),
          },
          {
            key: "historico",
            label: "Histórico e auditoria",
            content: <History l={l} />,
          },
          { key: "relatorio", label: "Relatório", content: <ReportView id={l.id} /> },
        ]}
      />
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/cargas" className="text-sm font-medium text-primary hover:underline">
      ← Cargas e acertos
    </Link>
  );
}

/** Cartões de resumo: carregado, vendido, esperado de volta, contado e diferenças. */
function SummaryCards({ l }: { l: LoadDetail }) {
  const t = settlementTotals(l.settlement);
  const d = divergences(l.settlement);
  const amount = (v: string) => (t.unit ? qty(v, t.unit) : qty(v));
  const parts = [
    ...d.shortage.map((x) => `falta ${qty(x.quantity)} ${x.unit}`),
    ...d.surplus.map((x) => `sobra ${qty(x.quantity)} ${x.unit}`),
    ...(d.cashShortage.length > 0 ? [`falta ${formatMoney(d.cashShortageTotal)}`] : []),
    ...(d.cashSurplus.length > 0 ? [`sobra ${formatMoney(d.cashSurplusTotal)}`] : []),
  ];
  const countHint =
    l.settlement.stage === "finalizado"
      ? "gravado no acerto"
      : `${t.countedLines} de ${t.lines} linha(s) informada(s)`;
  return (
    <section aria-label="Resumo da carga" className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
      <SummaryCard
        label="Carregado"
        value={amount(t.loaded)}
        hint={`${t.products} produto(s)${t.lots ? ` · ${t.lots} lote(s)` : ""}`}
      />
      <SummaryCard
        label="Vendido"
        value={amount(t.sold)}
        hint={`${l.orderCount} pedido(s) · ${money(l.salesTotal)}`}
      />
      <SummaryCard
        label="Esperado de volta"
        value={amount(t.expected)}
        hint="calculado: carregado − vendido"
      />
      <SummaryCard
        label="Contado"
        value={t.countedLines === 0 ? "—" : amount(t.counted)}
        hint={countHint}
        tone={t.countedLines < t.lines && l.settlement.stage !== "finalizado" ? "warn" : "ok"}
      />
      <SummaryCard
        label="Diferenças"
        value={parts.length === 0 ? "Nenhuma" : `${parts.length} item(ns)`}
        hint={parts.length === 0 ? "até agora" : parts.join(" · ")}
        tone={parts.length === 0 ? "ok" : "error"}
        emphasize
      />
    </section>
  );
}

/** Quem declarou, conferiu, liberou e finalizou; acertos estornados; movimentos. */
function History({ l }: { l: LoadDetail }) {
  const s = l.settlement;
  const rows: [string, string | null, string | null][] = [
    ["Prestação de contas enviada pelo vendedor", s.declaredByEmail, s.declaredAt],
    ["Última gravação do retorno", s.registeredByEmail, s.registeredAt],
    ["Conferência confirmada", s.checkedByEmail, s.checkedAt],
    ["Divergência liberada", s.divergenceReleasedByEmail, s.divergenceReleasedAt],
    ["Acerto finalizado", s.confirmedByEmail, s.confirmedAt],
  ];
  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-2 font-semibold">Acerto {s.number ?? ""}</h3>
        {s.id === null ? (
          <p className="text-sm text-muted-foreground">Nenhum acerto iniciado.</p>
        ) : (
          <Table head={["Etapa", "Quem", "Quando"]}>
            {rows.map(([label, who, when]) => (
              <tr key={label}>
                <td className="px-3 py-2">{label}</td>
                <td className="px-3 py-2">{who ?? "—"}</td>
                <td className="px-3 py-2">{dateTimeBR(when)}</td>
              </tr>
            ))}
          </Table>
        )}
        {(s.checkReason || s.divergenceReason) && (
          <div className="mt-3 space-y-1 text-sm">
            {s.checkReason && <p>Justificativa da conferência: {s.checkReason}</p>}
            {s.divergenceReason && <p>Motivo da liberação: {s.divergenceReason}</p>}
          </div>
        )}
        {s.status && (
          <p className="mt-2 text-xs text-muted-foreground">
            Situação: {SETTLEMENT_STATUS_LABEL[s.status]}
            {s.origin ? ` · ${SETTLEMENT_ORIGIN_LABEL[s.origin]}` : ""}
          </p>
        )}
      </div>
      {s.history.length > 0 && (
        <div>
          <h3 className="mb-2 font-semibold">Acertos estornados (preservados)</h3>
          <ul className="ml-5 list-disc text-sm">
            {s.history.map((h) => (
              <li key={h.id}>
                {h.number}: prestação de contas {h.declaredByEmail ?? "—"} (
                {dateTimeBR(h.declaredAt)}); conferência {h.checkedByEmail ?? "—"} (
                {dateTimeBR(h.checkedAt)})
                {h.checkDivergence ? ` — diferença: ${h.checkReason ?? "—"}` : ""}; estornado em{" "}
                {dateTimeBR(h.reversedAt)} — {h.reversalReason ?? ""}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div>
        <h3 className="mb-2 font-semibold">Movimentos do estoque do vendedor</h3>
        {l.movements.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum movimento.</p>
        ) : (
          <MovementsTable rows={l.movements} />
        )}
      </div>
    </div>
  );
}

function ReverseSettlementGate({ l }: { l: LoadDetail }) {
  const can = useCan();
  // estorno tem permissão própria (separada de finalizar), e nunca do próprio acerto
  if (!can("cargas.acerto.estornar") || l.isOwn) return null;
  return <ReverseSettlement loadId={l.id} />;
}

function Shipments({ l }: { l: LoadDetail }) {
  const can = useCan();
  const [adding, setAdding] = useState(false);
  const canShip = l.status === "aberta" && can("cargas.carga.confirmar");
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Carregamentos</h3>
        {canShip && !adding && (
          <Button size="sm" onClick={() => setAdding(true)}>
            {l.shipments.length === 0 ? "Carregar" : "Carregamento complementar"}
          </Button>
        )}
      </div>
      {adding && <ShipmentForm l={l} onClose={() => setAdding(false)} />}
      {l.shipments.length === 0 ? (
        <p className="text-muted-foreground">Nenhum carregamento ainda.</p>
      ) : (
        l.shipments.map((s) => (
          <div key={s.id} className="space-y-2 rounded-md border p-3">
            <div className="flex flex-wrap items-center gap-4">
              <span className="font-medium">#{s.sequence}</span>
              <DocStatusBadge status={s.status} />
              <span>{dateTimeBR(s.confirmedAt)}</span>
              <span>{s.confirmedByEmail ?? ""}</span>
              {s.notes && <span>{s.notes}</span>}
            </div>
            {s.status === "estornado" && (
              <Alert tone="warn">
                Estornado em {dateTimeBR(s.reversedAt)}. Motivo: {s.reversalReason}
              </Alert>
            )}
            <Table head={["Produto", "Lote", "Validade", "Quantidade"]}>
              {s.lots.map((x) => (
                <tr key={`${x.productCode}-${x.lotCode ?? ""}`}>
                  <td className="px-3 py-2 font-mono">{x.productCode}</td>
                  <td className="px-3 py-2 font-mono">{x.lotCode ?? "—"}</td>
                  <td className="px-3 py-2">{x.lotExpiresOn ?? "—"}</td>
                  <td className="px-3 py-2 text-right">{qty(x.quantity)}</td>
                </tr>
              ))}
            </Table>
            {s.status === "confirmado" && canShip && (
              <ReverseShipment loadId={l.id} shipmentId={s.id} />
            )}
            {s.status === "confirmado" && (
              <EmissionPanel
                use="remessa_carga"
                sourceId={s.id}
                title={`Nota de remessa — carregamento #${s.sequence}`}
              />
            )}
          </div>
        ))
      )}
    </div>
  );
}

function ReverseShipment({ loadId, shipmentId }: { loadId: string; shipmentId: string }) {
  const reverse = useDocAction<{ reason: string }, LoadDetail>({
    path: () => `/cargas/${loadId}/carregamentos/${shipmentId}/estornar`,
    invalidate: INVALIDATE,
  });
  return (
    <ReverseAction
      label="Estornar carregamento"
      pending={reverse.isPending}
      error={reverse.error}
      onConfirm={(reason) => reverse.mutate({ reason })}
    />
  );
}

function ShipmentForm({ l, onClose }: { l: LoadDetail; onClose: () => void }) {
  const [lines, setLines] = useState<DocItemLine[]>([emptyDocItem(1)]);
  const [notes, setNotes] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const save = useDocAction<unknown, LoadDetail>({
    path: () => `/cargas/${l.id}/carregamentos`,
    invalidate: INVALIDATE,
    onDone: onClose,
    successMessage: () => "Carregamento registrado com sucesso.",
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = shipmentInputSchema.safeParse({ notes, items: docItemsBody(lines) });
    if (!parsed.success) {
      const i = parsed.error.issues[0];
      return setFormError(
        `${i?.path.length ? `Campo ${i.path.join(".")}: ` : ""}${i?.message ?? "Dados inválidos"}`,
      );
    }
    setFormError(null);
    save.mutate(parsed.data);
  }
  return (
    <form onSubmit={submit} className="space-y-3 rounded-md border border-primary/40 p-3">
      <p className="text-muted-foreground">
        Sai de {l.originWarehouseName} pelo lote que vence primeiro (nunca vencido) e entra com os
        mesmos lotes em {l.warehouseName}.
      </p>
      <DocItemsEditor lines={lines} onChange={setLines} />
      <Field label="Observações">
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>
          Confirmar carregamento
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
      </div>
      {formError && <Alert tone="error">{formError}</Alert>}
      <SaveError error={save.error} />
    </form>
  );
}

function Orders({ l }: { l: LoadDetail }) {
  return (
    <div>
      <h3 className="mb-2 font-semibold">Vendas da rota</h3>
      {l.orders.length === 0 ? (
        <p className="text-muted-foreground">
          Nenhuma venda. As vendas da rota são pedidos de venda ligados a esta carga (menu Pedidos
          de venda), lançados pelo vendedor durante a rota ou pelo escritório no acerto.
        </p>
      ) : (
        <Table head={["Pedido", "Cliente", "Pagamento", "Total", "Situação"]}>
          {l.orders.map((o) => (
            <tr key={o.id}>
              <td className="px-3 py-2 font-mono">{o.number}</td>
              <td className="px-3 py-2">{o.customerName}</td>
              <td className="px-3 py-2">
                {o.paymentMethodName ?? "—"}
                {o.receivableNumber ? ` · título ${o.receivableNumber}` : ""}
              </td>
              <td className="px-3 py-2 text-right">{money(o.totalAmount)}</td>
              <td className="px-3 py-2">
                <SalesStatusBadge status={o.status} />
              </td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

function CancelLoad({ l }: { l: LoadDetail }) {
  const can = useCan();
  const cancel = useDocAction<{ version: number; reason: string }, LoadDetail>({
    path: () => `/cargas/${l.id}/cancelar`,
    invalidate: INVALIDATE,
  });
  const empty =
    l.status === "aberta" &&
    l.shipments.every((s) => s.status !== "confirmado") &&
    l.orders.every((o) => o.status === "cancelado");
  if (!empty || !can("cargas.carga.criar")) return null;
  return (
    <ReverseAction
      label="Cancelar carga"
      description="A carga sem carregamento confirmado e sem venda é cancelada (fica no histórico). Informe o motivo."
      confirmLabel="Confirmar cancelamento"
      pending={cancel.isPending}
      error={cancel.error}
      onConfirm={(reason) => cancel.mutate({ version: l.version, reason })}
    />
  );
}

/**
 * Notas do acerto (Fase 7): retorno do que voltou (depois da conferência) e, com o acerto
 * fechado, falta e sobra. A API decide se cada uma é exigida pela operação fiscal.
 */
function SettlementFiscal({ s }: { s: Settlement }) {
  if (!s.id || s.status === "estornado") return null;
  const closed = s.status === "confirmado";
  const shortage = s.products.some((p) => Number(p.difference ?? 0) > 0);
  const surplus = s.products.some((p) => Number(p.difference ?? 0) < 0);
  return (
    <div className="space-y-3">
      {s.checkedAt !== null && (
        <EmissionPanel use="retorno_carga" sourceId={s.id} title="Nota de retorno da carga" />
      )}
      {closed && shortage && (
        <EmissionPanel use="divergencia_falta" sourceId={s.id} title="Nota da falta no acerto" />
      )}
      {closed && surplus && (
        <EmissionPanel use="divergencia_sobra" sourceId={s.id} title="Nota da sobra no acerto" />
      )}
    </div>
  );
}

function ReverseSettlement({ loadId }: { loadId: string }) {
  const reverse = useDocAction<{ reason: string }, LoadDetail>({
    path: () => `/cargas/${loadId}/acerto/estornar`,
    invalidate: INVALIDATE,
  });
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Estornar o acerto devolve a mercadoria ao estoque do vendedor e reabre a carga. Só é
        possível enquanto o vendedor não tiver carga posterior.
      </p>
      <ReverseAction
        label="Estornar acerto"
        pending={reverse.isPending}
        error={reverse.error}
        onConfirm={(reason) => reverse.mutate({ reason })}
      />
    </div>
  );
}

// ---------------------------------------------------------------- relatório
function ReportView({ id }: { id: string }) {
  const report = useQuery({
    queryKey: [...LOAD_KEY, "relatorio", id],
    queryFn: () => apiFetch<LoadReport>(`/cargas/${id}/relatorio`),
  });
  const r = report.data;
  if (report.error) return <Alert tone="error">{errorMessage(report.error)}</Alert>;
  if (!r) return <Spinner />;
  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <Button size="sm" variant="secondary" onClick={() => window.print()}>
          Imprimir
        </Button>
      </div>
      <div>
        <h3 className="mb-2 font-semibold">Mercadoria: levou, vendeu, voltou</h3>
        <Table
          head={[
            "Produto / lote",
            "Levou",
            "Vendeu",
            "Declarado (vendedor)",
            "Conferido (escritório)",
            "Voltou",
            "Diferença",
          ]}
        >
          {r.products.map((p) => (
            <ReportProduct key={p.productCode} p={p} />
          ))}
        </Table>
      </div>
      <div>
        <h3 className="mb-2 font-semibold">Clientes</h3>
        <Table head={["Cliente", "Pedidos", "Total", "À vista", "A prazo"]}>
          {r.customers.map((c) => (
            <tr key={c.customerName}>
              <td className="px-3 py-2">{c.customerName}</td>
              <td className="px-3 py-2">{c.orders}</td>
              <td className="px-3 py-2 text-right">{money(c.total)}</td>
              <td className="px-3 py-2 text-right">{money(c.aVista)}</td>
              <td className="px-3 py-2 text-right">{money(c.aPrazo)}</td>
            </tr>
          ))}
        </Table>
      </div>
      <div>
        <h3 className="mb-2 font-semibold">Recebimentos</h3>
        <Table head={["Forma", "Quem recebe", "Esperado", "Entregue / conferido", "Diferença"]}>
          {r.receipts.map((p) => (
            <tr key={p.paymentMethodId}>
              <td className="px-3 py-2">{p.paymentMethodName}</td>
              <td className="px-3 py-2">{PAYMENT_HANDLING_LABEL[p.handling]}</td>
              <td className="px-3 py-2 text-right">{money(p.expected)}</td>
              <td className="px-3 py-2 text-right">{money(p.delivered)}</td>
              <td className="px-3 py-2 text-right">{money(p.difference)}</td>
            </tr>
          ))}
        </Table>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        <span>Total vendido: {money(r.salesTotal)}</span>
        <span>A prazo (títulos): {money(r.aPrazoTotal)}</span>
        <span>Comissão prevista: {money(r.commission)}</span>
        <span>Dinheiro/cheque a entregar: {money(r.cashExpected)}</span>
        <span>Entregue: {money(r.cashDelivered)}</span>
        <span>Diferença de dinheiro: {money(r.cashDifference)}</span>
        <span>Diferença de mercadoria (custo): {money(r.goodsDifferenceValue)}</span>
        <span>
          Acerto: {r.settlement.number ?? "—"}
          {r.settlement.status ? ` (${SETTLEMENT_STATUS_LABEL[r.settlement.status]})` : ""}
        </span>
        <span>
          Prestação de contas: {r.settlement.declaredByEmail ?? "—"} (
          {dateTimeBR(r.settlement.declaredAt)}) · conferência: {r.settlement.checkedByEmail ?? "—"}{" "}
          ({dateTimeBR(r.settlement.checkedAt)})
          {r.settlement.checkReason ? ` — ${r.settlement.checkReason}` : ""}
        </span>
        {r.settlement.divergenceReleasedByEmail && (
          <span>
            Divergência liberada por {r.settlement.divergenceReleasedByEmail}:{" "}
            {r.settlement.divergenceReason}
          </span>
        )}
      </div>
    </div>
  );
}

function ReportProduct({ p }: { p: LoadReport["products"][number] }) {
  return (
    <>
      <tr>
        <td className="px-3 py-2">
          <span className="font-mono text-xs">{p.productCode}</span> {p.productName}
        </td>
        <td className="px-3 py-2 text-right">{qty(p.loaded, p.baseUnitCode)}</td>
        <td className="px-3 py-2 text-right">{qty(p.sold)}</td>
        <td className="px-3 py-2 text-right">{qty(p.declared)}</td>
        <td className="px-3 py-2 text-right">{qty(p.counted)}</td>
        <td className="px-3 py-2 text-right">{qty(p.returned)}</td>
        <td className="px-3 py-2 text-right">{qty(p.difference)}</td>
      </tr>
      {p.lots
        .filter((x) => x.lotCode !== null)
        .map((x) => (
          <tr key={x.lotCode} className="text-muted-foreground">
            <td className="px-3 py-1 pl-8">
              lote <span className="font-mono">{x.lotCode}</span>
            </td>
            <td className="px-3 py-1 text-right">{qty(x.loaded)}</td>
            <td className="px-3 py-1 text-right">{qty(x.sold)}</td>
            <td className="px-3 py-1 text-right" />
            <td className="px-3 py-1 text-right" />
            <td className="px-3 py-1 text-right">{qty(x.returned)}</td>
            <td className="px-3 py-1 text-right">{qty(x.difference)}</td>
          </tr>
        ))}
    </>
  );
}
